import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AgentConfiguration } from "@/components/candidates/types/agent";
import { toast } from "sonner";

export const useAgentConfigurations = () => {
  return useQuery({
    queryKey: ['agent-configurations'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found for agent configurations');
        throw new Error('User not authenticated');
      }

      console.log('Fetching agent configurations for user:', user.id);

      const { data, error } = await supabase
        .from('agent_configurations')
        .select(`
          *,
          agent_configuration_tools(
            tool_id,
            is_enabled,
            config
          ),
          agent_configuration_knowledge_bases(
            knowledge_base_id,
            is_enabled
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching agent configurations:', error);
        // Don't throw for empty results, just return empty array
        if (error.code === 'PGRST116') {
          console.log('No agent configurations found, returning empty array');
          return [];
        }
        throw error;
      }

      console.log('Agent configurations fetched:', data?.length || 0);

      return data?.map(config => ({
        id: config.id,
        name: config.name,
        description: config.description || '',
        model: {
          id: config.model_id || 'invenio-standard',
          name: 'Invenio Standard',
          provider: 'invenio' as const,
          description: 'Default model for specialized agents',
          capabilities: [],
          latency: 'medium' as const,
          costTier: 'medium' as const
        },
        knowledgeBases: config.agent_configuration_knowledge_bases?.map(kb => ({
          id: kb.knowledge_base_id,
          name: kb.knowledge_base_id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          description: '',
          documentCount: 0,
          lastUpdated: new Date(),
          isEnabled: kb.is_enabled
        })) || [],
        tone: config.tone || 'professional',
        isEnabled: config.is_enabled || false,
        trainingLevel: config.training_level || 'basic',
        avatar: config.avatar,
        capabilities: config.capabilities || [],
        category: config.category || 'specialized',
        tools: config.agent_configuration_tools?.map(tool => ({
          id: tool.tool_id,
          name: tool.tool_id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          provider: 'system',
          description: '',
          enabled: tool.is_enabled,
          usesMCP: false,
          config: tool.config || {},
          capabilities: [],
          requiredCredentials: []
        })) || [],
        prompt: config.prompt
      })) as AgentConfiguration[] || [];
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    retry: (failureCount, error) => {
      console.log('Agent configurations query retry attempt:', failureCount, error);
      // Only retry for network errors, not auth errors
      if (error.message?.includes('not authenticated')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useUpdateAgentConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Partial<AgentConfiguration> & { id: string }) => {
      const { data, error } = await supabase
        .from('agent_configurations')
        .update({
          name: config.name,
          description: config.description,
          model_id: config.model?.id,
          tone: config.tone,
          is_enabled: config.isEnabled,
          training_level: config.trainingLevel,
          avatar: config.avatar,
          capabilities: config.capabilities,
          prompt: config.prompt,
          updated_at: new Date().toISOString()
        })
        .eq('id', config.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating agent configuration:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-configurations'] });
      toast.success('Agent configuration updated successfully');
    },
    onError: (error) => {
      console.error('Error updating agent configuration:', error);
      toast.error('Failed to update agent configuration');
    }
  });
};
